/**
 * Boş / yükleniyor / hata durumları — **uzuv** mertebesi.
 *
 * Her listede AYNI bileşen kullanılır (docs/13 bölüm 7). Gerekçe: bu üç durum
 * en çok atlanan ve en çok "yazılım bozuk" izlenimi veren yerlerdir. Tek
 * bileşen olunca hiçbir ekranda unutulmaz.
 */

import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import { ApiHatasi } from '../lib/api';
import { Dugme } from './ui/Dugme';
import { cn } from '../lib/cn';

/** Yükleniyor — iskelet satırlar. Boş ekran yerine yapının önizlemesi gösterilir. */
export function Yukleniyor({ satir = 5 }: { satir?: number }) {
  return (
    <div className="flex flex-col gap-2 p-4" role="status" aria-live="polite">
      <span className="sr-only">Yükleniyor</span>
      {Array.from({ length: satir }, (_, sira) => (
        <div
          key={sira}
          className="h-9 animate-pulse rounded-[var(--radius-md)] bg-[var(--surface-sunken)]"
          // Satırlar aynı anda değil sırayla nefes alsın; tek blok "donmuş" görünür.
          style={{ animationDelay: `${sira * 90}ms` }}
        />
      ))}
    </div>
  );
}

export interface BosDurumProps {
  baslik: string;
  aciklama: string;
  eylem?: ReactNode;
  ikon?: ReactNode;
}

export function BosDurum({ baslik, aciklama, eylem, ikon }: BosDurumProps) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
      <span className="text-[var(--ink-faint)]" aria-hidden="true">
        {ikon ?? <Inbox size={32} strokeWidth={1.5} />}
      </span>
      <h3 className="text-lg font-semibold text-[var(--ink)]">{baslik}</h3>
      {/* Okunabilirlik sınırı: 65ch (doküman 4.2) — sabit px genişlik değil. */}
      <p className="max-w-[65ch] text-sm text-[var(--ink-muted)]">{aciklama}</p>
      {eylem}
    </div>
  );
}

export interface HataKutusuProps {
  hata: unknown;
  /** "Tekrar dene" düğmesi — verilmezse gösterilmez. */
  tekrarDene?: () => void;
  className?: string;
}

/**
 * Hata kutusu.
 *
 * Ham `err.message` GÖSTERİLMEZ; sunucunun `presentError` ile hazırladığı
 * üç parçalı mesaj gösterilir: ne oldu / neden / ne yapmalı. En önemli parça
 * "ne yapmalı" — kullanıcı atacağı adımı bilmeden ekrandan ayrılmamalı.
 */
export function HataKutusu({ hata, tekrarDene, className }: HataKutusuProps) {
  const apiHatasi = hata instanceof ApiHatasi ? hata : null;
  const baslik = apiHatasi?.baslik ?? 'İşlem tamamlanamadı';
  const mesaj =
    apiHatasi?.message ?? (hata instanceof Error ? hata.message : 'Beklenmeyen bir sorun oluştu.');
  const yapilacak = apiHatasi?.yapilacak;
  const denenebilir = apiHatasi?.tekrarDenenebilir ?? true;

  return (
    <div
      role="alert"
      className={cn(
        // İç yarıçap: kartın (14px) içinde durursa 10px kullanır (formül).
        'flex flex-col gap-3 rounded-[var(--radius-md)] p-4',
        'border border-[var(--state-bad)] bg-[var(--state-bad-soft)]',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <span className="-mt-px shrink-0 text-[var(--state-bad)]" aria-hidden="true">
          <AlertTriangle size={18} strokeWidth={2} />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-sm font-semibold text-[var(--state-bad)]">{baslik}</h3>
          <p className="text-sm text-[var(--ink)]">{mesaj}</p>
          {yapilacak && (
            <p className="text-sm font-medium text-[var(--ink)]">
              <span className="text-[var(--ink-muted)]">Yapmanız gereken: </span>
              {yapilacak}
            </p>
          )}
          {apiHatasi?.kod && (
            <p className="text-xs text-[var(--ink-faint)]">
              Destek kodu: <span className="rakam">{apiHatasi.kod}</span>
            </p>
          )}
        </div>
      </div>
      {tekrarDene && denenebilir && (
        <div className="flex">
          <Dugme boy="sm" gorunum="ikincil" ikon={<RefreshCw size={13} />} onClick={tekrarDene}>
            Tekrar dene
          </Dugme>
        </div>
      )}
    </div>
  );
}
